"use client";

import * as React from "react";

import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { Switch } from "@repo/ui/components/ui/switch";
import { cn } from "@repo/ui/lib/utils";

export interface SwitchFieldProps<T extends FieldValues = FieldValues> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "type"
> {
  label?: string;
  description?: string;
  error?: string;
  success?: string;
  control?: Control<T>;
  name?: Path<T>;
  version?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  switchClassName?: string;
  labelClassName?: string;
  labelPosition?: "left" | "right" | "top";
  descriptionClassName?: string;
  disabled?: boolean;
  testId?: string;
}

function SwitchFieldInner<T extends FieldValues = FieldValues>(
  {
    className,
    label,
    description,
    control,
    name,
    error,
    success,
    version,
    checked,
    onCheckedChange,
    switchClassName,
    labelClassName,
    labelPosition = "right",
    descriptionClassName,
    disabled = false,
    testId,
    ...props
  }: SwitchFieldProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  const renderWithLabelPosition = (switchEl: React.ReactNode, labelEl: React.ReactNode) => {
    switch (labelPosition) {
      case "left":
        return (
          <div className="flex items-center gap-2">
            {labelEl}
            {switchEl}
          </div>
        );
      case "right":
        return (
          <div className="flex items-center gap-2">
            {switchEl}
            {labelEl}
          </div>
        );
      case "top":
        return (
          <div className="flex flex-col items-start gap-1">
            {labelEl}
            {switchEl}
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2">
            {switchEl}
            {labelEl}
          </div>
        );
    }
  };

  const labelEl =
    label &&
    (control ? (
      <FormLabel
        className={cn(
          "text-brand-blue cursor-default text-sm font-normal select-none",
          labelClassName,
        )}
      >
        {label}
      </FormLabel>
    ) : (
      <span className={cn("text-sm font-medium select-none", labelClassName)}>{label}</span>
    ));

  // RHF mode
  if (control && name) {
    if (version === "form") {
      return (
        <FormField
          control={control}
          name={name}
          render={({ field }) => {
            const isChecked = !!field.value;

            return (
              <FormItem
                className={cn(
                  "border-brand-gray hover:border-brand-blue flex flex-col justify-between rounded-md border px-3 py-2 text-sm font-normal focus-visible:ring-0 focus-visible:ring-offset-0",
                  isChecked ? "bg-white" : "bg-[#F7F7F7]",
                  disabled && "cursor-not-allowed opacity-50",
                  className,
                )}
                data-testid={testId}
              >
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel
                      className={cn(
                        "text-brand-blue cursor-default text-sm font-normal select-none",
                        labelClassName,
                      )}
                    >
                      {label}
                    </FormLabel>
                  </div>

                  <FormControl>
                    <Switch
                      checked={isChecked}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                      aria-readonly={disabled}
                      className={cn(
                        "data-[state=checked]:bg-brand-blue data-[state=unchecked]:bg-brand-blue-30 cursor-pointer",
                        switchClassName,
                      )}
                      data-testid={`${testId}-switch`}
                    />
                  </FormControl>
                </div>

                {description && (
                  <FormDescription
                    className={cn("mt-2 text-xs text-gray-500", descriptionClassName)}
                  >
                    {description}
                  </FormDescription>
                )}
              </FormItem>
            );
          }}
        />
      );
    } else {
      return (
        <FormField<T>
          control={control}
          name={name}
          render={({ field, fieldState }) => {
            const switchEl = (
              <>
                <Switch
                  checked={!!field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                  aria-invalid={!!fieldState.error}
                  className={cn(
                    "data-[state=checked]:bg-brand-blue data-[state=unchecked]:bg-brand-blue-30 cursor-pointer",
                    switchClassName,
                  )}
                  data-testid={`${testId}-switch`}
                />
                <input type="checkbox" hidden {...field} />
              </>
            );

            return (
              <FormItem className={cn("flex flex-col gap-2", className)} data-testid={testId}>
                <FormControl>{renderWithLabelPosition(switchEl, labelEl)}</FormControl>

                {description && (
                  <FormDescription className={cn("text-xs text-gray-500", descriptionClassName)}>
                    {description}
                  </FormDescription>
                )}

                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }
  }

  // Standalone mode
  const switchEl = (
    <>
      <Switch
        checked={Boolean(checked)}
        onCheckedChange={onCheckedChange}
        className={cn(
          "data-[state=checked]:bg-brand-blue data-[state=unchecked]:bg-brand-blue-30 cursor-pointer",
          switchClassName,
        )}
        aria-invalid={!!error}
      />
      <input ref={ref} type="checkbox" hidden checked={checked} readOnly {...props} />
    </>
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {renderWithLabelPosition(switchEl, labelEl)}

      {description && (
        <p className={cn("text-xs text-gray-500", descriptionClassName)}>{description}</p>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
      {success && <p className="text-xs text-green-500">{success}</p>}
    </div>
  );
}

export const SwitchField = React.forwardRef(SwitchFieldInner) as <
  T extends FieldValues = FieldValues,
>(
  props: SwitchFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement;

(SwitchField as unknown as { displayName?: string }).displayName = "SwitchField";
